// src/pages/admin/CreateEditQuestionnaireTemplatePage.js
// UPDATED - June 29, 2025

import React, { useState } from 'react';
import {
    Box, Paper, Typography, Button, TextField, Select, MenuItem,
    IconButton, List, Divider, Chip, Switch,
    FormControl, InputLabel, Tooltip, DialogActions, FormControlLabel
} from '@mui/material';
import {
    Add as AddIcon, Delete as DeleteIcon, ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

const CreateEditQuestionnaireTemplatePage = ({ template, onSave, onClose }) => {
    
    // --- STATE MANAGEMENT ---
    const [templateName, setTemplateName] = useState(template?.name || '');
    const [templateVersion, setTemplateVersion] = useState(template?.version || '1.0');
    const [templateDescription, setTemplateDescription] = useState(template?.description || '');
    const [templateStatus, setTemplateStatus] = useState(template?.status || 'Draft');
    const [questions, setQuestions] = useState(template?.questions || []);

    // --- NEW: SAVE HANDLER ---
    const handleSave = () => {
        // Basic Validation
        if (!templateName.trim() || !templateVersion.trim()) {
            toast.error("Template Name and Version are required.");
            return;
        }
        if (questions.length === 0) {
            toast.error("A template must have at least one question.");
            return;
        }
        // Validate that option-based questions have at least 2 options
        const invalidOptionQuestions = questions.filter(
            q => questionTypeHasOptions(q.questionType) && (!q.options || q.options.filter(o => o.trim()).length < 2)
        );
        if (invalidOptionQuestions.length > 0) {
            toast.error("Dropdown, Multiple Choice, and Checkbox questions must have at least 2 options.");
            return;
        }

        const templateData = {
            name: templateName,
            version: templateVersion,
            description: templateDescription,
            status: templateStatus,
            questions: questions,
        };
        
        // Call the onSave prop passed from the parent component
        onSave(templateData);
    };


    // --- QUESTION HANDLERS ---
    const handleAddQuestion = (index) => {
        const newQuestion = { id: uuidv4(), sectionTitle: '', sectionDescription: '', questionText: '', questionType: 'Short Text', placeholderText: '', options: [], isRequired: false };
        const newQuestions = [...questions];
        newQuestions.splice(index + 1, 0, newQuestion);
        setQuestions(newQuestions);
    };

    const handleQuestionChange = (index, field, value) => {
        const newQuestions = [...questions];
        newQuestions[index][field] = value;
        setQuestions(newQuestions);
    };

    const handleDeleteQuestion = (index) => {
        if (questions.length === 0) return;
        const newQuestions = [...questions];
        newQuestions.splice(index, 1);
        setQuestions(newQuestions);
    };

    const handleMoveQuestion = (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === questions.length - 1) return;

        const newQuestions = [...questions];
        const questionToMove = newQuestions[index];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        newQuestions[index] = newQuestions[swapIndex];
        newQuestions[swapIndex] = questionToMove;
        setQuestions(newQuestions);
    };

    // --- OPTION HANDLERS (for Dropdown, Multiple Choice, Checkbox) ---
    const handleAddOption = (questionIndex) => {
        const newQuestions = [...questions];
        if (!newQuestions[questionIndex].options) {
            newQuestions[questionIndex].options = [];
        }
        newQuestions[questionIndex].options.push('');
        setQuestions(newQuestions);
    };

    const handleOptionChange = (questionIndex, optionIndex, value) => {
        const newQuestions = [...questions];
        newQuestions[questionIndex].options[optionIndex] = value;
        setQuestions(newQuestions);
    };

    const handleDeleteOption = (questionIndex, optionIndex) => {
        const newQuestions = [...questions];
        newQuestions[questionIndex].options.splice(optionIndex, 1);
        setQuestions(newQuestions);
    };

    // Helper: does this question type need options?
    const questionTypeHasOptions = (type) => {
        return ['Dropdown', 'Multiple Choice', 'Checkbox'].includes(type);
    };


    // --- RENDER LOGIC ---
    return (
        <Box>
            {/* TEMPLATE DETAILS FORM */}
            <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
                <Typography variant="h6" gutterBottom>Template Details</Typography>
                <Box component="div" noValidate autoComplete="off">
                    <TextField label="Template Name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} fullWidth required sx={{ mb: 2 }}/>
                    <TextField label="Version" value={templateVersion} onChange={(e) => setTemplateVersion(e.target.value)} fullWidth required sx={{ mb: 2 }} />
                    <TextField label="Description" value={templateDescription} onChange={(e) => setTemplateDescription(e.target.value)} fullWidth multiline rows={3} sx={{ mb: 2 }} />
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select value={templateStatus} label="Status" onChange={(e) => setTemplateStatus(e.target.value)}>
                            <MenuItem value="Draft">Draft</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Archived">Archived</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {/* DYNAMIC QUESTIONS SECTION */}
            <Paper sx={{ p: 3 }} variant="outlined">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Questions</Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddQuestion(questions.length - 1)}>
                        Add Question
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <List sx={{ p: 0 }}>
                    {questions.length > 0 ? questions.map((q, index) => (
                        <Paper key={q.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" color="text.secondary">Question #{index + 1}</Typography>
                                <Box>
                                    <Tooltip title="Move Up"><IconButton onClick={() => handleMoveQuestion(index, 'up')} disabled={index === 0}><ArrowUpwardIcon /></IconButton></Tooltip>
                                    <Tooltip title="Move Down"><IconButton onClick={() => handleMoveQuestion(index, 'down')} disabled={index === questions.length - 1}><ArrowDownwardIcon /></IconButton></Tooltip>
                                    <Tooltip title="Delete Question"><IconButton onClick={() => handleDeleteQuestion(index)}><DeleteIcon color="error" /></IconButton></Tooltip>
                                    <Tooltip title="Add Question Below"><IconButton onClick={() => handleAddQuestion(index)} color="primary"><AddIcon /></IconButton></Tooltip>
                                </Box>
                            </Box>
                            <TextField label="Section Title (Optional)" value={q.sectionTitle} onChange={(e) => handleQuestionChange(index, 'sectionTitle', e.target.value)} fullWidth variant="filled" sx={{ mb: 2 }} />
                            <TextField label="Section Description / Guidance (Optional)" value={q.sectionDescription || ''} onChange={(e) => handleQuestionChange(index, 'sectionDescription', e.target.value)} fullWidth variant="filled" multiline rows={2} sx={{ mb: 2 }} placeholder="e.g., This section is optional. If you plan to upload full financials, you may skip these questions." />
                            <TextField label="Question Text" value={q.questionText} onChange={(e) => handleQuestionChange(index, 'questionText', e.target.value)} fullWidth multiline required rows={3} variant="filled" sx={{ mb: 2 }} />
                             <FormControl variant="filled" fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Question Type</InputLabel>
                                <Select value={q.questionType} onChange={(e) => handleQuestionChange(index, 'questionType', e.target.value)} label="Question Type">
                                    <MenuItem value="Short Text">Short Text</MenuItem>
                                    <MenuItem value="Long Text">Long Text</MenuItem>
                                    <MenuItem value="Dropdown">Dropdown (Single Select)</MenuItem>
                                    <MenuItem value="Multiple Choice">Multiple Choice (Radio)</MenuItem>
                                    <MenuItem value="Checkbox">Checkbox (Multi-Select)</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Options Editor - shown for Dropdown, Multiple Choice, Checkbox */}
                            {questionTypeHasOptions(q.questionType) && (
                                <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.light' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Answer Options</Typography>
                                    {(q.options || []).map((option, optIndex) => (
                                        <Box key={optIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Chip label={optIndex + 1} size="small" />
                                            <TextField
                                                value={option}
                                                onChange={(e) => handleOptionChange(index, optIndex, e.target.value)}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                placeholder={`Option ${optIndex + 1}`}
                                            />
                                            <Tooltip title="Remove Option">
                                                <IconButton size="small" onClick={() => handleDeleteOption(index, optIndex)}>
                                                    <DeleteIcon fontSize="small" color="error" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ))}
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddOption(index)} sx={{ mt: 1 }}>
                                        Add Option
                                    </Button>
                                </Box>
                            )}

                            {/* Required Toggle */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={q.isRequired || false}
                                        onChange={(e) => handleQuestionChange(index, 'isRequired', e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Required"
                            />
                        </Paper>
                    )) : (
                        <Typography sx={{ my: 3, textAlign: 'center', color: 'text.secondary' }}>
                            No questions yet. Click "Add Question" to begin.
                        </Typography>
                    )}
                </List>
            </Paper>

            {/* --- NEW: MOVED DIALOG ACTIONS HERE --- */}
            <DialogActions sx={{ p: '16px 0', mt: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>Create & Save Template</Button>
            </DialogActions>
        </Box>
    );
};

export default CreateEditQuestionnaireTemplatePage;